"use client";

import { useMemo, useState } from "react";
import {
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { BarChart } from "@/components/tools/stats-distributions/charts";
import {
  DistributionTable,
  type MeaningLabels,
  ProbabilitySummary,
} from "@/components/tools/stats-distributions/discrete-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import { logChoose } from "@/lib/stats/special";
import { barGeometry } from "@/lib/tool-kit/stats-distributions/chart";
import {
  type DiscreteModel,
  discreteProbabilities,
  distributionTable,
  tableRange,
} from "@/lib/tool-kit/stats-distributions/discrete";
import type { NumberFormatter } from "@/lib/tool-kit/stats-distributions/format";
import {
  type NegativeBinomialConvention,
  negativeBinomialModel,
  negativeBinomialQuantile,
  toRFailures,
} from "@/lib/tool-kit/stats-distributions-more/negative-binomial";

/** English strings. A translated content file overrides these through `ui`. */
export const NEGATIVE_BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS = {
  conventionLabel: "What are you counting?",
  conventionTrials: "Trials needed to reach the r-th success (X = r, r + 1, …)",
  conventionFailures:
    "Failures before the r-th success (Y = 0, 1, 2, …) — R's dnbinom",
  rLabel: "Number of successes you are waiting for (r)",
  pLabel: "Probability of success on each trial (p)",
  kTrialsLabel: "Trial on which the r-th success happens (k)",
  kFailuresLabel: "Number of failures before the r-th success (k)",
  k2Label: "Upper end of a range (optional)",
  k2Hint: "Adds the probability of a value from k up to this number.",
  results: "Results",
  probability: "Probability",
  meaning: "Meaning",
  value: "Value",
  trialsEqual: "r-th success on exactly trial {k}",
  trialsAtMost: "r-th success by trial {k}",
  trialsLessThan: "r-th success before trial {k}",
  trialsAtLeast: "r-th success on trial {k} or later",
  trialsGreaterThan: "r-th success after trial {k}",
  trialsBetween: "r-th success between trials {a} and {b}, inclusive",
  failuresEqual: "failure count is exactly {k}",
  failuresAtMost: "failure count is at most {k}",
  failuresLessThan: "failure count is below {k}",
  failuresAtLeast: "failure count is at least {k}",
  failuresGreaterThan: "failure count is above {k}",
  failuresBetween: "failure count from {a} to {b}, inclusive",
  meanTrials: "Mean (r/p)",
  meanFailures: "Mean (r(1 − p)/p)",
  variance: "Variance (r(1 − p)/p²)",
  sd: "Standard deviation",
  median: "Median",
  workingHeading: "How this was calculated",
  pmfTrials:
    "P(X = k) = C(k − 1, r − 1) × p^r × (1 − p)^(k − r) = C({n}, {m}) × {p}^{r} × (1 − {p})^{f} = {coefficient} × {p}^{r} × (1 − {p})^{f} = {value}",
  pmfFailures:
    "P(Y = y) = C(y + r − 1, y) × p^r × (1 − p)^y = C({n}, {m}) × {p}^{r} × (1 − {p})^{f} = {coefficient} × {p}^{r} × (1 − {p})^{f} = {value}",
  cdfLine:
    "The cumulative probability is the regularised incomplete beta function: P(at most {f} failures) = I_p(r, {f} + 1) = I_{p}({r}, {a}) = {value}.",
  rTrials:
    "In R: dnbinom({y}, {r}, {p}) = {pmf} and pnbinom({y}, {r}, {p}) = {cdf}. R's size is r and it counts failures before the r-th success, so the r-th success on trial {k} is {y} failures.",
  rFailures:
    "In R: dnbinom({k}, {r}, {p}) = {pmf} and pnbinom({k}, {r}, {p}) = {cdf}. R's size is r and it counts failures before the r-th success, which is the convention you chose, so the numbers carry straight over.",
  rMu: "R can also take the mean number of failures instead of p: dnbinom(x, size = {r}, mu = {mu}) gives the same distribution, since prob = size / (size + mu) = {p}.",
  otherTrials:
    "Counting failures instead (Y = X − r), the mean is {mean} and the variance is the same, {variance}. P(X = {k}) equals P(Y = {y}).",
  otherFailures:
    "Counting trials instead (X = Y + r), the mean is {mean} and the variance is the same, {variance}. P(Y = {k}) equals P(X = {x}).",
  chartHeading: "Probability distribution",
  chartLabel:
    "Bar chart of the negative binomial probabilities for values from {from} to {to}, with the bars your question covers drawn darker",
  tableCaption:
    "Negative binomial probabilities for r = {r}, p = {p}, values {from} to {to}",
  tableLimit:
    "The table and chart are drawn while the distribution fits in about 200 rows; for a small p or a large r use the probabilities above.",
  enterR: "Enter r as a whole number from 1 to 100,000.",
  enterP: "Enter p greater than 0 and at most 1, such as 0.4.",
  enterK: "Enter k as a whole number of 0 or more.",
} as const;

type Strings = typeof NEGATIVE_BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS;

const MAX_K = 1_000_000_000;
const MAX_R = 100_000;

interface Inputs {
  r: number;
  p: number;
  k: number;
  k2: number | null;
}

function readInputs(
  rText: string,
  pText: string,
  kText: string,
  k2Text: string
): Inputs | keyof Strings {
  const r = parseIntegerInput(rText);
  if (r === null || r < 1 || r > MAX_R) {
    return "enterR";
  }
  const p = parseNumberInput(pText);
  if (p === null || p <= 0 || p > 1) {
    return "enterP";
  }
  const k = parseIntegerInput(kText);
  const hasSecond = k2Text.trim() !== "";
  const k2 = hasSecond ? parseIntegerInput(k2Text) : null;
  const valid = (value: number | null) =>
    value !== null && value >= 0 && value <= MAX_K;
  if (!valid(k) || (hasSecond && !valid(k2))) {
    return "enterK";
  }
  return { r, p, k: k as number, k2 };
}

function labelsFor(
  convention: NegativeBinomialConvention,
  strings: Strings
): MeaningLabels {
  const base = {
    probability: strings.probability,
    meaning: strings.meaning,
    value: strings.value,
  };
  if (convention === "trials") {
    return {
      ...base,
      equal: strings.trialsEqual,
      atMost: strings.trialsAtMost,
      lessThan: strings.trialsLessThan,
      atLeast: strings.trialsAtLeast,
      greaterThan: strings.trialsGreaterThan,
      between: strings.trialsBetween,
    };
  }
  return {
    ...base,
    equal: strings.failuresEqual,
    atMost: strings.failuresAtMost,
    lessThan: strings.failuresLessThan,
    atLeast: strings.failuresAtLeast,
    greaterThan: strings.failuresGreaterThan,
    between: strings.failuresBetween,
  };
}

function Working({
  inputs,
  convention,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  convention: NegativeBinomialConvention;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { r, p, k } = inputs;
  const four = (value: number) =>
    fmt(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const trials = convention === "trials";
  const y = toRFailures(k, r, convention);
  const inSupport = y >= 0;
  const failures = Math.max(0, y);
  const coefficient = Math.exp(logChoose(failures + r - 1, failures));
  const base = { r: fmt(r), p: sig(p), k: fmt(k) };
  const meanFailures = (r * (1 - p)) / p;
  return (
    <div className={`${CARD} space-y-2`}>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      {inSupport ? (
        <>
          <p className="break-words font-mono text-muted-foreground text-sm">
            {t(trials ? strings.pmfTrials : strings.pmfFailures, {
              ...base,
              n: fmt(trials ? k - 1 : failures + r - 1),
              m: fmt(trials ? r - 1 : failures),
              f: fmt(failures),
              coefficient: fmt(coefficient, { maximumSignificantDigits: 8 }),
              value: four(model.pmf(k)),
            })}
          </p>
          <p className="break-words font-mono text-muted-foreground text-sm">
            {t(strings.cdfLine, {
              ...base,
              f: fmt(failures),
              a: fmt(failures + 1),
              value: four(model.cdf(k)),
            })}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(trials ? strings.rTrials : strings.rFailures, {
              ...base,
              y: fmt(y),
              pmf: four(model.pmf(k)),
              cdf: four(model.cdf(k)),
            })}
          </p>
          <p className="text-muted-foreground text-sm">
            {t(trials ? strings.otherTrials : strings.otherFailures, {
              mean: sig(trials ? meanFailures : r / p),
              variance: sig(model.variance),
              k: fmt(k),
              y: fmt(k - r),
              x: fmt(k + r),
            })}
          </p>
        </>
      ) : null}
      <p className="text-muted-foreground text-sm">
        {t(strings.rMu, { r: fmt(r), p: sig(p), mu: sig(meanFailures) })}
      </p>
    </div>
  );
}

function Distribution({
  inputs,
  model,
  strings,
  fmt,
}: {
  inputs: Inputs;
  model: DiscreteModel;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const { k, k2, p, r } = inputs;
  const range = useMemo(
    () => tableRange(model, k2 === null ? [k] : [k, k2]),
    [model, k, k2]
  );
  const rows = useMemo(
    () => (range ? distributionTable(model, range[0], range[1]) : []),
    [model, range]
  );
  const geometry = useMemo(
    () => barGeometry(rows.map((row) => ({ k: row.k, p: row.pmf }))),
    [rows]
  );
  if (!range) {
    return <Notice>{strings.tableLimit}</Notice>;
  }
  const low = k2 === null ? k : Math.min(k, k2);
  const high = k2 === null ? k : Math.max(k, k2);
  const highlighted = (value: number) => value >= low && value <= high;
  const span = { from: fmt(range[0]), to: fmt(range[1]) };
  return (
    <>
      <div className={`${CARD} space-y-3`}>
        <h3 className={WIDGET_H3}>{strings.chartHeading}</h3>
        <BarChart
          format={(value) => fmt(value)}
          geometry={geometry}
          highlighted={highlighted}
          label={t(strings.chartLabel, span)}
        />
      </div>
      <DistributionTable
        caption={t(strings.tableCaption, {
          ...span,
          r: fmt(r),
          p: fmt(p, { maximumSignificantDigits: 5 }),
        })}
        fmt={fmt}
        highlighted={highlighted}
        rows={rows}
      />
    </>
  );
}

export default function NegativeBinomialDistributionCalculatorWidget() {
  const strings = useToolUi(NEGATIVE_BINOMIAL_DISTRIBUTION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [convention, setConvention] =
    useState<NegativeBinomialConvention>("trials");
  const [rText, setRText] = useState("3");
  const [pText, setPText] = useState("0.4");
  const [kText, setKText] = useState("7");
  const [k2Text, setK2Text] = useState("");

  const inputs = readInputs(rText, pText, kText, k2Text);
  const r = typeof inputs === "object" ? inputs.r : 1;
  const p = typeof inputs === "object" ? inputs.p : 0.5;
  const model = useMemo(
    () => negativeBinomialModel(r, p, convention),
    [r, p, convention]
  );
  const sig = (value: number) => fmt(value, { maximumSignificantDigits: 5 });
  const trials = convention === "trials";

  return (
    <>
      <section className="space-y-5">
        <PillGroup
          label={strings.conventionLabel}
          name="nb-convention"
          onChange={setConvention}
          options={[
            { value: "trials", label: strings.conventionTrials },
            { value: "failures", label: strings.conventionFailures },
          ]}
          value={convention}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="nb-r"
            label={strings.rLabel}
            numeric
            onChange={setRText}
            value={rText}
          />
          <TextField
            id="nb-p"
            label={strings.pLabel}
            numeric
            onChange={setPText}
            value={pText}
          />
          <TextField
            id="nb-k"
            label={trials ? strings.kTrialsLabel : strings.kFailuresLabel}
            numeric
            onChange={setKText}
            value={kText}
          />
          <TextField
            hint={strings.k2Hint}
            id="nb-k2"
            label={strings.k2Label}
            numeric
            onChange={setK2Text}
            value={k2Text}
          />
        </div>
      </section>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {typeof inputs === "string" ? (
          <Notice>{strings[inputs]}</Notice>
        ) : (
          <>
            <ProbabilitySummary
              fmt={fmt}
              k={inputs.k}
              labels={labelsFor(convention, strings)}
              probabilities={discreteProbabilities(model, inputs.k, inputs.k2)}
            />
            <StatGrid>
              <StatCard
                label={trials ? strings.meanTrials : strings.meanFailures}
                value={sig(model.mean)}
              />
              <StatCard label={strings.variance} value={sig(model.variance)} />
              <StatCard
                label={strings.sd}
                value={sig(Math.sqrt(model.variance))}
              />
              <StatCard
                label={strings.median}
                value={fmt(
                  negativeBinomialQuantile(0.5, inputs.r, inputs.p, convention)
                )}
              />
            </StatGrid>
            <Working
              convention={convention}
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
            <Distribution
              fmt={fmt}
              inputs={inputs}
              model={model}
              strings={strings}
            />
          </>
        )}
      </section>
    </>
  );
}
